/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

import {
  selectRemoteConfig,
  selectShouldShowActivities,
} from '../../../../services/firebase/remote-config/remoteConfig.slice';
import TestIds from '../../../../testIds';
import { ScrollYPositionKeys } from '../../../../util/consts';
import NavigationMenuItem from '../../../../components/app-navigation/NavigationMenu/NavigationMenuItem';
import { NavigationStyledMenuList } from '../../../../components/app-navigation/NavigationMenu/NavigationStyledMenuList';
import { getDeclarations } from '../../../declarations/declarations.slice';
import { useAppDispatch } from '../../../../store/rootStore';
import DeclarationModal from '../../../declarations/components/DeclarationsModal';
import { ORDERS_ROUTE } from '../../../orders/orders.routes';
import { AGREEMENTS_ROUTE } from '../../../sales-agreements/sales-agreements-routes';
import { QUOTES_ROUTE } from '../../../quotes/quotes.routes';
import { LOGOUT_CE_USER_ROUTE, LOGOUT_ROUTE } from '../../../../util/routing';
import { ACCOUNT_ACCOUNT_DETAILS_ROUTE } from '../../account.routes';
import { ACTIVITIES_ROUTE } from '../../../activities/activities-routes';
import {
  DigitalFarmerAccount,
  selectCurrentFarmerAccount,
  selectIsCEUser,
  selectIsThisTfo,
} from '../../../azure-b2c/storage';

export interface IFeatureLinksProps {
  enableAccountDetails?: boolean;
  enableQuotes?: boolean;
  enableAgreements?: boolean;
  enableDeclarations?: boolean;
  enableActivities?: boolean;
  enableOrders?: boolean;
  enableLogout?: boolean;
  divider?: boolean;
}

const FeatureLinks: FC<IFeatureLinksProps> = ({
  enableAccountDetails,
  enableQuotes,
  enableAgreements,
  enableDeclarations,
  enableActivities,
  enableOrders,
  enableLogout,
  divider,
}: IFeatureLinksProps): JSX.Element | undefined => {
  const selectedAccount: DigitalFarmerAccount | undefined = useSelector(selectCurrentFarmerAccount);
  const remoteConfig = useSelector(selectRemoteConfig);
  const shouldShowActivities = useSelector(selectShouldShowActivities);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isTfo = useSelector(selectIsThisTfo);
  const isCEUser = useSelector(selectIsCEUser);

  const [openDeclarationsModal, setOpenDeclarationsModal] = useState(false);

  return (
    <NavigationStyledMenuList
      data-testid={TestIds.feat.account.userAccount.featureLinks.component}
      role='menu'
    >
      {selectedAccount && (
        <>
          {enableAccountDetails && (
            <NavigationMenuItem
              data-testid={TestIds.feat.account.userAccount.featureLinks.accountDetails}
              variant='farmlands2.0_default'
              color='primary'
              label='Account Details'
              prefixMuiIcon={<SettingsOutlinedIcon />}
              scrollYPosition={ScrollYPositionKeys.SelectedAccountDetails}
              onClick={() => navigate(ACCOUNT_ACCOUNT_DETAILS_ROUTE)}
              divider={divider}
            />
          )}

          {remoteConfig.quoteEnabled && enableQuotes && (
            <NavigationMenuItem
              data-testid={TestIds.feat.account.userAccount.featureLinks.quotes}
              variant='farmlands2.0_default'
              color='primary'
              label='Quotes'
              prefixMuiIcon='request_quote'
              scrollYPosition={ScrollYPositionKeys.Quotes}
              onClick={() => navigate(QUOTES_ROUTE)}
              divider={divider}
            />
          )}

          {enableAgreements && (
            <NavigationMenuItem
              data-testid={TestIds.feat.account.userAccount.featureLinks.salesAgreements}
              variant='farmlands2.0_default'
              color='primary'
              label='Agreements'
              prefixMuiIcon='Note'
              scrollYPosition={ScrollYPositionKeys.Agreements}
              onClick={() => navigate(AGREEMENTS_ROUTE)}
              divider={divider}
            />
          )}

          {enableDeclarations && (
            <NavigationMenuItem
              data-testid={TestIds.feat.account.userAccount.featureLinks.declarations}
              variant='farmlands2.0_default'
              color='primary'
              label='Declarations'
              prefixMuiIcon='folder'
              scrollYPosition={ScrollYPositionKeys.Orders}
              onClick={() => {
                setOpenDeclarationsModal(true);
                dispatch(getDeclarations());
              }}
              divider={divider}
            >
              <DeclarationModal
                open={openDeclarationsModal}
                handleClose={() => setOpenDeclarationsModal(false)}
              />
            </NavigationMenuItem>
          )}

          {shouldShowActivities && enableActivities && isTfo && (
            <NavigationMenuItem
              data-testid={TestIds.feat.account.userAccount.featureLinks.activities}
              variant='farmlands2.0_default'
              color='primary'
              label='Activities'
              prefixMuiIcon='calendar_month'
              scrollYPosition={ScrollYPositionKeys.Activities}
              onClick={() => navigate(ACTIVITIES_ROUTE)}
              divider={divider}
            />
          )}

          {enableOrders && (
            <NavigationMenuItem
              data-testid={TestIds.feat.account.userAccount.featureLinks.orders}
              variant='farmlands2.0_default'
              color='primary'
              label='Orders'
              prefixMuiIcon='contract'
              scrollYPosition={ScrollYPositionKeys.Orders}
              onClick={() => navigate(ORDERS_ROUTE)}
              divider
            />
          )}
        </>
      )}

      {enableLogout && (
        <NavigationMenuItem
          data-testid={TestIds.feat.account.userAccount.featureLinks.logout}
          variant='farmlands2.0_default'
          color='primary'
          label='Log out'
          prefixMuiIcon={<LogoutOutlinedIcon />}
          scrollYPosition={ScrollYPositionKeys.Orders}
          onClick={() => navigate(isCEUser ? LOGOUT_CE_USER_ROUTE : LOGOUT_ROUTE)}
        />
      )}
    </NavigationStyledMenuList>
  );
};

export default FeatureLinks;
